import React, { useEffect, useContext, useRef } from 'react';
import FadeIn from 'react-fade-in';
import { Observer } from 'mobx-react-lite';
import MaskedInput from 'react-text-mask'
import { GlobalHotKeys } from 'react-hotkeys';

import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import { quickDrawerFocus } from '../../_shared/QuickDrawer';
import AddressInput from '../../_shared/AddressInput';
import HtmlEditor from '../../_shared/HtmlEditor';

import ContactCreateStore from '../../../../stores/ContactCreateStore';
import AuthStore from '../../../../stores/AuthStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as MaskKeys from '../../../../constants/maskKeys';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';

import './AddContact.scss';

function AddContact(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const searchTimer = useRef(null);
    const focusTimer = useRef(null);
    const nameGroupRef = useRef(null);
    const nameLabelRef = useRef(null);
    const companyNameGroupRef = useRef(null);
    const companyNameLabelRef = useRef(null);
    const addressRef = useRef(null);
    const contact = useContext(ContactCreateStore);
    const auth = useContext(AuthStore);
    const quickDrawer = useContext(QuickDrawerStore);

    useEffect(() => {
        focusTimer.current = setTimeout(() => {
            quickDrawerFocus(props.drawer);
        }, 100)

        return () => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
            if (searchTimer.current) { clearTimeout(searchTimer.current); searchTimer.current = null; }
            contact.clear();
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (contact.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        if (fn.validateForm(validateRef.current) && addressRef.current.validate()) {
            contact.save()
                .then((data) => {
                    if (isMounted.current) {
                        if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                            props.onSuccess(event, { updated: true, data: data });
                        }
                    }
                })
        }
    }

    const handleAddressChange = (data) => {
        contact.data.address = data;
        contact.hasUnsavedChanges = true;
    }

    const handleNameChange = (event) => {
        const value = event.target.value;

        contact.data.name = value;
        contact.hasUnsavedChanges = true;
    }

    const handleCompanyNameChange = (event) => {
        const value = event.target.value;

        contact.data.companyName = value;
        contact.hasUnsavedChanges = true;
    }

    const handlePrimaryPhoneNumberChange = (event) => {
        const value = event.target.value;

        contact.data.primaryPhoneNumber = value;
        contact.hasUnsavedChanges = true;
    }

    const handleSecondaryPhoneNumberChange = (event) => {
        const value = event.target.value;

        contact.data.secondaryPhoneNumber = value;
        contact.hasUnsavedChanges = true;
    }

    const handleFaxNumberChange = (event) => {
        const value = event.target.value;

        contact.data.faxNumber = value;
        contact.hasUnsavedChanges = true;
    }

    const handlePrimaryEmailAddressChange = (event) => {
        const value = event.target.value;

        contact.data.primaryEmailAddress = value;
        contact.hasUnsavedChanges = true;
    }

    const handleSecondaryEmailAddressChange = (event) => {
        const value = event.target.value;

        contact.data.secondaryEmailAddress = value;
        contact.hasUnsavedChanges = true;
    }

    const handleWebsiteChange = (event) => {
        const value = event.target.value;

        contact.data.websiteUrl = value;
        contact.hasUnsavedChanges = true;
    }

    const handleNoteChange = (element) => {
        const newNoteHtml = element.value;
        contact.data.noteHtml = newNoteHtml;
    }

    return <>
        {
            (props.drawer === quickDrawer.drawerOpened) ?
                <GlobalHotKeys
                    keyMap={{
                        close: ['esc'],
                    }}
                    handlers={{
                        close: event => {
                            handleCancel(event)
                        },
                    }}
                    allowChanges={true}
                /> : null
        }
        <form ref={validateRef} onSubmit={handleSubmit}>
            <fieldset disabled={contact.isSaving}>
                <div className='add-contact-container quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('contact', 'default')}
                        action='Add new'
                        category='Contact'
                        className='contacts'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <FadeIn>
                            <div className='body-content'>
                                <section>
                                    <div className='row'>
                                        <div className='col-12'>
                                            <Observer>{() => <>
                                                <div ref={nameGroupRef} className={'form-group mb-0' + (!contact.data.companyName ? ' validate validate-required' : '')}>
                                                    <label ref={nameLabelRef} className={(!contact.data.companyName ? 'required' : '')} htmlFor='new-contact-name'><small>Name</small></label>

                                                    <input
                                                        id='new-contact-name'
                                                        type='text'
                                                        className='form-control'
                                                        spellCheck={true}
                                                        maxLength='255'
                                                        autoComplete='off'
                                                        value={contact.data.name ? contact.data.name : ''}
                                                        onChange={handleNameChange}
                                                    />
                                                </div>
                                            </>}</Observer>
                                        </div>
                                    </div>
                                </section>
                                <section>
                                    <div className='row'>
                                        <div className='col-12'>
                                            <Observer>{() => <>
                                                <div ref={companyNameGroupRef} className={'form-group mb-0' + (!contact.data.name ? ' validate validate-required' : '')}>
                                                    <label ref={companyNameLabelRef} className={(!contact.data.name ? 'required' : '')} htmlFor='new-contact-company'><small>Company</small></label>
                                                    <input
                                                        id='new-contact-company'
                                                        type='text'
                                                        className='form-control'
                                                        spellCheck={true}
                                                        maxLength='255'
                                                        autoComplete='off'
                                                        value={contact.data.companyName ? contact.data.companyName : ''}
                                                        onChange={handleCompanyNameChange}
                                                    />
                                                </div>
                                            </>}</Observer>
                                        </div>
                                    </div>
                                </section>
                                <section>
                                    <AddressInput
                                        ref={addressRef}
                                        address={(contact.data && contact.data.address ? contact.data.address : {})}
                                        label={'Address'}
                                        defaultCountryCode={(auth.currentUser && auth.currentTenant && auth.currentTenant.address ? auth.currentTenant.address.countryCode : null)}
                                        onChange={handleAddressChange}
                                    />
                                </section>
                                <section>
                                    <div className='row'>
                                        <div className='col-6'>
                                            <div className='form-group mb-0 validate validate-phone'>
                                                <label htmlFor='new-contact-primary-phone'><small>Primary Phone</small></label>
                                                <Observer>{() => <>
                                                    <MaskedInput
                                                        id='new-contact-primary-phone'
                                                        type='text'
                                                        className='form-control'
                                                        spellCheck={false}
                                                        mask={MaskKeys.PHONE_MASK}
                                                        autoComplete='off'
                                                        value={contact.data.phoneNumber ? contact.data.phoneNumber : ''}
                                                        onChange={handlePrimaryPhoneNumberChange}
                                                    // onBlur={checkConflicts}
                                                    />
                                                </>}</Observer>
                                            </div>
                                        </div>
                                        <div className='col-6'>
                                            <div className='form-group mb-0 validate validate-phone'>
                                                <label htmlFor='new-contact-secondary-phone'><small>Secondary Phone</small></label>
                                                <Observer>{() => <>
                                                    <MaskedInput
                                                        id='new-contact-secondary-phone'
                                                        type='text'
                                                        className='form-control'
                                                        spellCheck={false}
                                                        mask={MaskKeys.PHONE_MASK}
                                                        autoComplete='off'
                                                        value={contact.data.phoneNumber ? contact.data.phoneNumber : ''}
                                                        onChange={handleSecondaryPhoneNumberChange}
                                                    // onBlur={checkConflicts}
                                                    />
                                                </>}</Observer>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <section>
                                    <div className='row'>
                                        <div className='col-12'>
                                            <div className='form-group mb-0 validate validate-phone'>
                                                <label htmlFor='new-contact-fax'><small>Fax</small></label>
                                                <Observer>{() => <>
                                                    <MaskedInput
                                                        id='new-contact-fax'
                                                        type='text'
                                                        className='form-control'
                                                        spellCheck={false}
                                                        mask={MaskKeys.PHONE_MASK}
                                                        autoComplete='off'
                                                        value={contact.data.faxNumber ? contact.data.faxNumber : ''}
                                                        onChange={handleFaxNumberChange}
                                                    // onBlur={checkConflicts}
                                                    />
                                                </>}</Observer>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <section>
                                    <div className='row'>
                                        <div className='col-12'>
                                            <div className='form-group mb-0 validate validate-email'>
                                                <label htmlFor='new-contact-primary-email'><small>Primary Email</small></label>
                                                <Observer>{() => <>
                                                    <MaskedInput
                                                        id='new-contact-primary-email'
                                                        type='text'
                                                        className='form-control'
                                                        spellCheck={false}
                                                        mask={MaskKeys.EMAIL_MASK}
                                                        maxLength='150'
                                                        autoComplete='off'
                                                        value={contact.data.primaryEmailAddress ? contact.data.primaryEmailAddress : ''}
                                                        onChange={handlePrimaryEmailAddressChange}
                                                    // onBlur={checkConflicts}
                                                    />
                                                </>}</Observer>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <section>
                                    <div className='row'>
                                        <div className='col-12'>
                                            <div className='form-group mb-0 validate validate-email'>
                                                <label htmlFor='new-contact-secondary-email'><small>Secondary Email</small></label>
                                                <Observer>{() => <>
                                                    <MaskedInput
                                                        id='new-contact-secondary-email'
                                                        type='text'
                                                        className='form-control'
                                                        spellCheck={false}
                                                        mask={MaskKeys.EMAIL_MASK}
                                                        maxLength='150'
                                                        autoComplete='off'
                                                        value={contact.data.secondaryEmailAddress ? contact.data.secondaryEmailAddress : ''}
                                                        onChange={handleSecondaryEmailAddressChange}
                                                    // onBlur={checkConflicts}
                                                    />
                                                </>}</Observer>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <section>
                                    <div className='row'>
                                        <div className='col-12'>
                                            <div className='form-group mb-0'>
                                                <label htmlFor='new-contact-website'><small>Website</small></label>
                                                <Observer>{() => <>
                                                    <input
                                                        id='new-contact-website'
                                                        type='text'
                                                        className='form-control'
                                                        spellCheck={false}
                                                        maxLength='255'
                                                        autoComplete='off'
                                                        value={contact.data.websiteUrl ? contact.data.websiteUrl : ''}
                                                        onChange={handleWebsiteChange}
                                                    // onBlur={checkConflicts}
                                                    />
                                                </>}</Observer>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                                <section>
                                    <div className='row'>
                                        <div className='col-12'>
                                            <div className='form-group mb-2'>
                                                <label htmlFor='new-contact-note'><small>Note</small></label>
                                                <Observer>{() => <>
                                                    <HtmlEditor
                                                        disableTab={true}
                                                        onValueChanged={handleNoteChange}
                                                    />
                                                </>}</Observer>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </FadeIn>
                    </div>
                    <div className='quick-drawer-action pl-3'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-link btn-cancel mr-2'
                                        onClick={handleCancel}
                                    >Cancel</button>
                                    <button
                                        type='submit'
                                        className='btn btn-success'
                                    >Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
    </>
}

export default AddContact;